import { computed, onUnmounted, ref, type Ref } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import type StaticContentTableOfContentsProps from '@/ui/components/interfaces/StaticContentTableOfContentsProps'

export default function useStaticContentHeadings(content: Ref<string>) {
  const activeHeadingId = ref('')
  let observer: IntersectionObserver | null = null

  const headings = computed<Array<StaticContentTableOfContentsProps>>(() => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(content.value, 'text/html')
    return Array.from(doc.querySelectorAll('h1')).map((el) => ({
      uuid: uuidv4(),
      title: el.textContent ?? ''
    }))
  })

  const processedContent = computed(() => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(content.value, 'text/html')
    doc.querySelectorAll('h1').forEach((el, index) => {
      const heading = headings.value[index]
      if (heading) el.setAttribute('id', heading.uuid)
    })
    return doc.body.innerHTML
  })

  function observeHeadings(containerEl: HTMLElement) {
    observer?.disconnect()

    observer = new IntersectionObserver(
      (entries) => {
        const topmost = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]

        if (topmost) activeHeadingId.value = topmost.target.id
      },
      { threshold: 0, rootMargin: '0px 0px -80% 0px' }
    )

    containerEl.querySelectorAll('h1[id]').forEach((el) => observer!.observe(el))
  }

  onUnmounted(() => observer?.disconnect())

  return { headings, processedContent, activeHeadingId, observeHeadings }
}
